import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface GrazingAngleConfig {
  min: number;
  max: number;
}

interface ConfigurationSettings {
  grazingAngle: GrazingAngleConfig;
  polarization: string;
}

interface GrazingAngleFieldsProps {
  configurationSettingsData: {
    grazingAngle?: {
      min: number;
      max: number;
    };
    polarization?: string;
    id: string;
    name: string;
  };
  setConfigurationSettingsData: React.Dispatch<
    React.SetStateAction<{
      id: string;
      name: string;
      bitDepth?: string;
      projections?: string;
      radioMetricProcessing?: string;
      grazingAngle?: {
        min: number;
        max: number;
      };
      polarization?: string;
    }>
  >;
}

const polarizationOptions = ['VV', 'HV', 'VH', 'HH'] as const;
type PolarizationType = (typeof polarizationOptions)[number];

function GrazingAngleFields({
  configurationSettingsData,
  setConfigurationSettingsData,
}: GrazingAngleFieldsProps) {
  const { t } = useTranslation('provider');

  const handleMinAngleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const data = {
      ...configurationSettingsData,
      grazingAngle: {
        min: Number(e.target.value),
        max: configurationSettingsData?.grazingAngle?.max ?? 70,
      },
    };
    setConfigurationSettingsData(data);
  };

  const handleMaxAngleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const data = {
      ...configurationSettingsData,
      grazingAngle: {
        min: configurationSettingsData?.grazingAngle?.min ?? 0,
        max: Number(e.target.value),
      },
    };
    setConfigurationSettingsData(data);
  };

  const handlePolarizationChange = (event: SelectChangeEvent<string>) => {
    const data = {
      ...configurationSettingsData,
      polarization: event.target.value as PolarizationType,
    };
    setConfigurationSettingsData(data);
  };

  return (
    <Stack gap={2}>
      <Typography sx={{ fontSize: '16px' }} variant="subtitle2">
        {t('grazingAngle')}
      </Typography>
      <Box gap={1} justifyContent="center" display="flex" flexDirection="row">
        <TextField
          id="grazing-angle-min"
          fullWidth
          type="number"
          label={t('minimum')}
          variant="filled"
          aria-valuemax={70}
          aria-valuemin={1}
          slotProps={{
            htmlInput: {
              min: 0,
              max: 70,
            },
          }}
          value={configurationSettingsData?.grazingAngle?.min}
          onChange={handleMinAngleChange}
        />
        <TextField
          id="grazing-angle-max"
          fullWidth
          aria-valuemax={70}
          aria-valuemin={1}
          label={t('maximum')}
          variant="filled"
          slotProps={{
            htmlInput: {
              min: 1,
              max: 70,
              'aria-label': 'max-grazing-angle',
            },
          }}
          defaultValue="70"
          type="number"
          value={configurationSettingsData?.grazingAngle?.max}
          onChange={handleMaxAngleChange}
        />
      </Box>
      <Typography sx={{ fontSize: '16px' }} variant="subtitle2">
        {t('polarization')}
      </Typography>
      <FormControl variant="filled" fullWidth>
        <InputLabel id="polarization-select-label">{t('select')}</InputLabel>
        <Select
          labelId="polarization-select-label"
          id="polarization-select"
          label={t('captureMethod')}
          value={configurationSettingsData?.polarization}
          onChange={handlePolarizationChange}
          MenuProps={{
            className: 'select-menu',
          }}
          slotProps={{
            input: {
              'aria-label': 'polarization-select',
            },
          }}
        >
          {polarizationOptions?.map(item => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}

export default GrazingAngleFields;
